import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import IconSave from "../../components/Icon/IconSave";
import IconX from "../../components/Icon/IconX";
import IconEdit from "../../components/Icon/IconEdit";
import IconTrash from "../../components/Icon/IconTrash";
import adService from "../../service/ad.service";

const AddAd = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        adImg: null,
        adImgUrl:"",
        targetUrl: "",
    });
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [ads, setAds] = useState([]);
    const [selectedAd, setSelectedAd] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Show preview when image URL changes
        if (name === "adImgUrl" && value.trim()) {
            setImagePreview(value);
        } else if (name === "adImgUrl" && !value.trim()) {
            setImagePreview(null);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData((prev) => ({
                ...prev,
                adImg: file,
            }));

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    };

    // Load existing ads on component mount
    useEffect(() => {
        loadExistingAds();
    }, []);

    const loadExistingAds = async () => {
        try {
            setPageLoading(true);
            const response = await adService.getAllAds();
            
            if (response.data && response.data.data) {
                setAds(response.data.data);
            } else {
                setAds([]);
            }
        } catch (error) {
            setAds([]);
        } finally {
            setPageLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // At least one field should be provided
        if (!formData.targetUrl.trim() && !formData.adImgUrl.trim() && !formData.adImg) {
            const toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                showCloseButton: true,
                customClass: {
                    popup: `color-danger`,
                },
            });
            toast.fire({
                title: "Please provide a target URL, image URL, or upload an image file",
            });
            return;
        }

        try {
            setLoading(true);

             // Create FormData for file upload
             const submitData = new FormData();
             if (formData.targetUrl.trim()) {
                 submitData.append("targetUrl", formData.targetUrl);
             }

             if (formData.adImg) {
                 submitData.append("adImg", formData.adImg);
             }

            let response;
            if (isEditMode && selectedAd) {
                // Update existing ad
                response = await adService.updateAd(selectedAd.id, submitData);
            } else {
                // Create new ad
                response = await adService.createAd(submitData);
            }

            const toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                showCloseButton: true,
                customClass: {
                    popup: `color-success`,
                },
            });
            toast.fire({
                title: isEditMode ? "Ad updated successfully" : "Ad created successfully",
            });

            // Reload existing ads to refresh the data
            await loadExistingAds();
            // Reset form and hide it
            resetForm();

        } catch (error) {
            
            // Extract error message from API response
            let errorMessage = isEditMode ? "Failed to update ad" : "Failed to create ad";
            if (error.response && error.response.data && error.response.data.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            const toast = Swal.mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                showCloseButton: true,
                customClass: {
                    popup: `color-danger`,
                },
            });
            toast.fire({
                title: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (adId) => {
        // Show confirmation dialog
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                await adService.deleteAd(adId);
                
                const toast = Swal.mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    showCloseButton: true,
                    customClass: {
                        popup: `color-success`,
                    },
                });
                toast.fire({
                    title: "Ad deleted successfully",
                });

                // Reload ads to refresh the list
                await loadExistingAds();
                // If we were editing this ad, reset form
                if (selectedAd && selectedAd.id === adId) {
                    resetForm();
                }

            } catch (error) {
                
                // Extract error message from API response
                let errorMessage = "Failed to delete ad";
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }
                
                const toast = Swal.mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                    showCloseButton: true,
                    customClass: {
                        popup: `color-danger`,
                    },
                });
                toast.fire({
                    title: errorMessage,
                });
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEdit = (ad) => {
        setSelectedAd(ad);
        setFormData({
            adImg: null,
            adImgUrl: ad.adImgUrl || "",
            targetUrl: ad.targetUrl || "",
        });
        setIsEditMode(true);
        setShowForm(true);
        
        // Set preview for existing ad image URL
        if (ad.adImgUrl) {
            setImagePreview(ad.adImgUrl);
        } else {
            setImagePreview(null);
        }
    };

    const handleAddNew = () => {
        resetForm();
        setShowForm(true);
    };

    const resetForm = () => {
        setSelectedAd(null);
        setIsEditMode(false);
        setShowForm(false);
        setFormData({
            adImg: null,
            adImgUrl: "",
            targetUrl: "",
        });
        setImagePreview(null);
    };

    const handleCancel = () => {
        resetForm();
    };

    if (pageLoading) {
        return (
            <div className="min-h-screen p-4 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4" >
            <div className="w-full max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-xl font-bold text-gray-800 dark:text-white">
                        Banner Management
                    </h1>
                    <div className="flex items-center gap-3">
                        {!showForm && (
                            <button
                                type="button"
                                onClick={handleAddNew}
                                className="btn btn-primary flex items-center gap-2"
                            >
                                <IconSave className="w-4 h-4" />
                                Add New Ad
                            </button>
                        )}
                        {showForm && (
                            <button
                                type="button"
                                onClick={handleCancel}
                                className="btn btn-outline-secondary flex items-center gap-2"
                            >
                                <IconX className="w-4 h-4" />
                                Cancel
                            </button>
                        )}
                    </div>
                </div>

                {/* Ads List */}
                {!showForm && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                        {ads.length === 0 ? (
                            <div className="col-span-full text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
                                <p className="text-gray-600 dark:text-gray-400 mb-4">No advertisements found</p>
                                <button
                                    onClick={handleAddNew}
                                    className="btn btn-primary"
                                >
                                    Create First Ad
                                </button>
                            </div>
                        ) : (
                            ads.map((ad) => (
                                <div key={ad.id} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg">
                                    <div className="mb-4">
                                        {ad.adImgUrl ? (
                                            <img
                                                src={ad.adImgUrl}
                                                alt="Advertisement"
                                                className="w-full h-40 object-cover rounded-lg"
                                            />
                                        ) : (
                                            <div className="w-full h-40 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                                                <p className="text-gray-500 dark:text-gray-400">No Image</p>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mb-4">
                                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                            {ad.targetUrl || 'No target URL'}
                                        </p>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEdit(ad)}
                                            className="btn btn-outline-primary btn-sm flex items-center gap-1"
                                        >
                                            <IconEdit className="w-3 h-3" />
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(ad.id)}
                                            disabled={loading}
                                            className="btn btn-outline-danger btn-sm flex items-center gap-1"
                                        >
                                            <IconTrash className="w-3 h-3" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Form */}
                {showForm && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-8" style={{
                        boxShadow: 'var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow)',
                        '--tw-shadow': 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
                        '--tw-shadow-colored': '0px 4px 12px var(--tw-shadow-color)'
                    }}>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-6">
                                {isEditMode ? "Edit Advertisement" : "Add New Advertisement"}
                            </h2>

                            {/* Target URL */}
                            <div className="mb-6">
                                <label htmlFor="targetUrl" className="block text-sm font-semibold text-gray-800 mb-2">
                                    Target URL
                                </label>
                                <input
                                    type="url"
                                    id="targetUrl"
                                    name="targetUrl"
                                    value={formData.targetUrl}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 bg-gray-100 border-2 rounded-xl text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-0 shadow-sm"
                                    placeholder="Enter target URL"
                                />
                            </div>

                            {/* Ad Image File Upload */}
                            <div className="mb-6">
                                <label htmlFor="adImg" className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                                    Image File
                                </label>
                                <input
                                    type="file"
                                    id="adImg"
                                    name="adImg"
                                    onChange={handleImageChange}
                                    accept="image/*"
                                    className="w-full px-4 py-3 bg-gray-100 border-2 rounded-xl text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-0 shadow-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-gray-600 file:text-white hover:file:bg-gray-700"
                                />

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="mt-4">
                                        <div className="border-0 rounded-xl p-4 bg-gray-100 dark:bg-gray-700 shadow-sm">
                                            <img
                                                src={imagePreview}
                                                alt="Ad preview"
                                                className="w-full h-32 object-cover rounded-lg"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <div className="flex justify-end pt-6">
                                <button
                                    type="submit"
                                    className="btn btn-primary flex items-center gap-3 shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            {isEditMode ? "Updating..." : "Creating..."}
                                        </>
                                    ) : (
                                        <>
                                            {isEditMode ? <IconEdit className="w-4 h-4" /> : <IconSave className="w-4 h-4" />}
                                            {isEditMode ? "Update Ad" : "Create Ad"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddAd;
